/* 0.49.54 - Values handed to the next page through the server, never in
 * the address (backend services/value_handover.py).
 *
 * A page or a flow set to take its Address values "from the previous
 * page, through the server" (or every one of them, with the home page's
 * switch "Values in addresses: off") is listed by /api/public/handover/
 * targets. A link to it is written as before, with its values
 * (/hy/services/apply?training={column:id}): on a click the site sends
 * the values to the server and opens the clean address
 * (/hy/services/apply). The target takes them on arrival, once.
 *
 * The browser's history entry is marked (state.handover), so the target
 * only asks when it was opened that way. A refresh asks again and finds
 * nothing (taken), and a bookmark, a forwarded link or a typed address
 * has no mark at all: the target sends the visitor to the page its
 * admin chose. Back to the entry in the same tab finds the values kept
 * here for that entry. */
let targetsPromise = null;
let targetsCache = null;

const post = (url, body) => fetch(url, {
  method: 'POST', credentials: 'include', cache: 'no-store',
  headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
}).then(r => (r.ok ? r.json() : null));

export function loadTargets() {
  if (targetsCache) return Promise.resolve(targetsCache);
  if (!targetsPromise) {
    targetsPromise = fetch('/api/public/handover/targets', { credentials: 'include', cache: 'no-store' })
      .then(r => (r.ok ? r.json() : null))
      .then((d) => {
        targetsCache = { off: !!(d && d.off), pages: new Set((d && d.pages) || []), flows: new Set((d && d.flows) || []),
          // 0.49.59 - The site's page for Link incomplete ('' the message).
          linkIncompletePage: String((d && d.link_incomplete_page) || '') };
        return targetsCache;
      })
      .catch(() => { targetsPromise = null; return { off: false, pages: new Set(), flows: new Set(), linkIncompletePage: '' }; });
  }
  return targetsPromise;
}

/** { kind, slug } for /:lang/p/:slug and /:lang/services/:slug[/step],
 *  and the same without the language (0.49.57: /p/apply, which the site
 *  sends on to the visitor's language). */
export function targetOf(path) {
  const m = /^(?:\/[A-Za-z-]{2,10})?\/(p|services)\/([A-Za-z0-9_-]+)(?:\/[^/?#]*)?\/?$/.exec(path || '');
  return m ? { kind: m[1] === 'p' ? 'page' : 'flow', slug: m[2] } : null;
}

/** The address to open for `to` (a router address with ?values), after
 *  handing its values over when the target takes them through the
 *  server: { to, state }. Anything else comes back as it was. */
export async function prepareMove(to) {
  const qAt = to.indexOf('?');
  if (qAt < 0) return { to, state: undefined };
  const hashAt = to.indexOf('#', qAt);
  const path = to.slice(0, qAt);
  const search = to.slice(qAt, hashAt < 0 ? undefined : hashAt);
  const hash = hashAt < 0 ? '' : to.slice(hashAt);
  const t = await loadTargets();
  const target = targetOf(path);
  const server = target && (target.kind === 'page' ? t.pages : t.flows).has(target.slug);
  if (!server && !t.off) return { to, state: undefined };
  // With values in addresses off, a value never travels in one.
  if (!server) return { to: path + hash, state: undefined };
  const values = {};
  try { for (const [k, v] of new URLSearchParams(search).entries()) if (!(k in values)) values[k] = v; } catch (_e) { /* none */ }
  try { await post('/api/public/handover', { kind: target.kind, slug: target.slug, values }); } catch (_e) { /* the target finds nothing */ }
  return { to: path + hash, state: { handover: Date.now() } };
}

const taken = new Map();

/** The values handed to this target for the history entry `entryKey`,
 *  or null when it was not opened by a hand-over. */
export function takeHandover(kind, slug, entryKey, marked) {
  const key = `${kind}:${slug}:${entryKey}`;
  if (taken.has(key)) return taken.get(key);
  if (!marked) return Promise.resolve(null);
  const p = post('/api/public/handover/take', { kind, slug, values: {} })
    .then(d => (d && d.values && typeof d.values === 'object' ? d.values : {}))
    .catch(() => ({}));
  taken.set(key, p);
  return p;
}

/** Where a visitor without a required value goes: null to stay (the
 *  Link incomplete page or message), else a router address. */
export function missingTarget(setting, language) {
  const v = String(setting || '').trim();
  if (!v) return null;
  if (v === 'home') return '/';
  return /^[A-Za-z0-9_-]+$/.test(v) ? `/${language}/p/${v}` : null;
}
